import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <Path3D>: a Node3D that holds a Curve3D. It always provides the tessellated curve through
 * Path3DCurveProvider, so a PathFollow3D child follows it whatever the selection or the Gizmos
 * toggle. It draws the curve as a white polyline gizmo for every node, faint while unselected, like
 * Godot's 3D editor (`path_3d_editor_plugin.cpp:320`, ADR-0056).
 */
import { useMemo } from 'react';
import { Node3D } from '../../base/node3d/Component';
import { GizmoLine } from '../../../r3f/components/GizmoLine';
import { PluginGizmo } from '../../../r3f/gizmo/PluginGizmo';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { resolveCurve3D, tessellateCurve3D } from '../../../resources/curves/curve3d';
import { Path3DCurveProvider } from '../../../r3f/contexts/Path3DCurveContext';
/** Godot editor path line colour. */
const PATH_COLOR = 0xffffff;
export function Path3D({ node, children }) {
    const props = node.properties;
    const { internalResources } = useSceneResources();
    // Godot 3D space is three.js space (right-handed Y-up), so curve points need no conjugation,
    // unlike the 2D twin. A missing, ExtResource or unfound curve gives a null sampler:
    // no gizmo, and children keep their authored transform.
    const sampler = useMemo(() => {
        const points = resolveCurve3D(props.curve, internalResources);
        if (points.length < 2)
            return null;
        return tessellateCurve3D(points);
    }, [props.curve, internalResources]);
    return (_jsxs(Node3D, { node: node, children: [sampler && (_jsx(PluginGizmo, { drawnFor: "every-node", children: _jsx(PathCurveGizmo, { sampler: sampler }) })), _jsx(Path3DCurveProvider, { value: sampler, children: children })] }));
}
function PathCurveGizmo({ sampler }) {
    const positions = useMemo(() => buildPolylineSegments(sampler.points), [sampler]);
    return _jsx(GizmoLine, { positions: positions, color: PATH_COLOR });
}
/** Turn the flat polyline `[x0,y0,z0,…]` into LineSegments vertex position pairs. */
function buildPolylineSegments(points) {
    const vertexCount = Math.floor(points.length / 3);
    const segCount = Math.max(0, vertexCount - 1);
    const positions = new Float32Array(segCount * 2 * 3);
    let o = 0;
    for (let i = 0; i + 1 < vertexCount; i++) {
        positions[o++] = points[i * 3];
        positions[o++] = points[i * 3 + 1];
        positions[o++] = points[i * 3 + 2];
        positions[o++] = points[(i + 1) * 3];
        positions[o++] = points[(i + 1) * 3 + 1];
        positions[o++] = points[(i + 1) * 3 + 2];
    }
    return positions;
}
//# sourceMappingURL=Component.js.map